"use client";

/**
 * LinkifiedMessage
 * Renders a chatbot message body as plain text with links made clickable.
 * Shared by the public ChatbotWidget and the admin chat-history viewer so both
 * surfaces render bot replies (and their `[label](url)` links) the same way,
 * without `dangerouslySetInnerHTML`.
 */

import { Fragment, memo, useMemo } from "react";
import { linkifyMessage } from "../../_logic/linkify-message";

interface LinkifiedMessageProps {
  /** Raw message text (may include `\n` line breaks and Markdown links). */
  content: string;
  /** Extra classes for the `<a>` elements (e.g. color overrides per surface). */
  linkClassName?: string;
  /**
   * Optional click interceptor for a rendered link. Called with the link's
   * href; return `true` to signal the click was handled in-app (the default
   * navigation / new-tab open is then suppressed via `preventDefault`), or
   * `false`/undefined to let the link open normally. The widget uses this to
   * open form and hotel-booking links as an embedded modal instead of a new tab.
   */
  onLinkClick?: (href: string) => boolean;
}

/**
 * `linkifyMessage` is non-trivial (regex parse + per-line splitting) and the
 * messages list can re-render on every keystroke, so parsing is memoized per
 * `content`.
 *
 * @param content - the raw message string to render
 * @param linkClassName - optional extra classes applied to each rendered link
 * @param onLinkClick - optional interceptor; return true to handle a link in-app
 * @returns the message with URLs and Markdown links rendered as anchors
 */
export const LinkifiedMessage = memo(function LinkifiedMessage({
  content,
  linkClassName,
  onLinkClick,
}: LinkifiedMessageProps) {
  const lines = useMemo(() => linkifyMessage(content), [content]);
  const anchorClass = `underline wrap-break-word ${linkClassName ?? ""}`.trim();

  return (
    <>
      {lines.map((line, lineIdx, allLines) => (
        <Fragment key={lineIdx}>
          {line.map((segment, segIdx) =>
            segment.kind === "url" ? (
              <a
                key={segIdx}
                href={segment.value}
                target="_blank"
                rel="noopener noreferrer"
                className={anchorClass}
                onClick={(event) => {
                  // Let modified clicks (new tab / new window) fall through untouched.
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  if (onLinkClick?.(segment.value)) event.preventDefault();
                }}
              >
                {segment.label ?? segment.value}
              </a>
            ) : (
              <Fragment key={segIdx}>{segment.value}</Fragment>
            )
          )}
          {lineIdx < allLines.length - 1 && <br />}
        </Fragment>
      ))}
    </>
  );
});
